<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Eye, Sparkles } from '@lucide/vue'
import { Badge, Button, EmptyState, Input, Section, Spinner, Table, useToast } from '@drama/ui'

import { searchIntentVideos } from '../../api/intent-videos.js'
import { formatMs, similarityColor, statusLabel, statusVariant } from './format.js'

const props = defineProps({ scenarioId: { type: String, required: true } })

const { t } = useI18n()
const toast = useToast()
const router = useRouter()

const text = ref('')
const searching = ref(false)
const result = ref(null)

const columns = computed(() => [
  { key: 'similarity', label: t('visualGameAdmin.intentVideo.table.similarity'), width: '110px', mono: true },
  { key: 'status', label: t('visualGameAdmin.intentVideo.table.status'), width: '100px' },
  { key: 'intent', label: t('visualGameAdmin.intentVideo.table.intent'), width: '320px' },
  { key: 'resolution', label: t('visualGameAdmin.intentVideo.table.resolution'), width: '120px', mono: true },
  { key: 'duration', label: t('visualGameAdmin.intentVideo.table.duration'), width: '90px', align: 'right', mono: true },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '90px', align: 'right', fixed: 'right' },
])

async function search() {
  if (text.value.trim() === '') return
  searching.value = true
  try {
    result.value = await searchIntentVideos({ scenario_id: props.scenarioId, text: text.value.trim(), limit: 20 })
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    searching.value = false
  }
}

function openDetail(row) {
  router.push({ name: 'intent-video-recall-detail', params: { resourceId: row.id } })
}
</script>

<template>
  <div class="ivt vga-page" data-insp-path="apps/visual-game-admin/src/pages/intent-video/RecallTestTab.vue#recall">
    <Section>
      <div class="ivt__box">
        <Input
          v-model="text"
          type="search"
          size="sm"
          class="ivt__input"
          :placeholder="t('visualGameAdmin.intentVideo.recall.placeholder')"
          @keyup.enter="search"
        />
        <Button size="sm" :loading="searching" @click="search">
          <Sparkles :size="14" /> {{ t('visualGameAdmin.intentVideo.recall.run') }}
        </Button>
      </div>
      <p class="ivt__hint">{{ t('visualGameAdmin.intentVideo.recall.hint') }}</p>
    </Section>

    <div v-if="searching" class="vga-state">
      <Spinner /><span>{{ t('visualGameAdmin.intentVideo.recall.running') }}</span>
    </div>
    <template v-else-if="result">
      <Section>
        <div class="ivt__meta">
          <span>{{ t('visualGameAdmin.intentVideo.recall.query') }}: <strong>{{ result.query }}</strong></span>
          <span>embedding call: <code>{{ result.embedding_call_id }}</code></span>
          <span v-if="result.embedding_duration_ms">{{ formatMs(result.embedding_duration_ms) }}</span>
          <span>{{ t('visualGameAdmin.intentVideo.recall.hits', { n: result.results.length }) }}</span>
        </div>
      </Section>
      <Section :padded="false">
        <EmptyState
          v-if="result.results.length === 0"
          :title="t('visualGameAdmin.intentVideo.recall.noHitTitle')"
          :description="t('visualGameAdmin.intentVideo.recall.noHitDescription')"
        />
        <Table v-else :columns="columns" :rows="result.results" clickable @row-click="openDetail">
          <template #similarity="{ row }">
            <span class="ivt__sim" :style="{ color: similarityColor(row.similarity) }">{{ row.similarity.toFixed(3) }}</span>
          </template>
          <template #status="{ row }">
            <Badge :variant="statusVariant(row.status)" size="sm">{{ statusLabel(t, row.status) }}</Badge>
          </template>
          <template #intent="{ row }">
            <span class="ivt__ellipsis" :title="row.intent">{{ row.intent }}</span>
          </template>
          <template #resolution="{ row }">
            <template v-if="row.video_width">{{ row.video_width }}×{{ row.video_height }}</template>
            <template v-else>-</template>
          </template>
          <template #duration="{ row }">{{ formatMs(row.video_duration_ms) }}</template>
          <template #actions="{ row }">
            <Button variant="secondary" size="sm" @click.stop="openDetail(row)">
              <Eye :size="14" /> {{ t('visualGameAdmin.common.view') }}
            </Button>
          </template>
        </Table>
      </Section>
    </template>
    <Section v-else>
      <EmptyState
        :title="t('visualGameAdmin.intentVideo.recall.emptyTitle')"
        :description="t('visualGameAdmin.intentVideo.recall.emptyDescription')"
      />
    </Section>
  </div>
</template>

<style scoped>
.ivt__box { display: flex; gap: var(--drama-space-sm); align-items: center; }
.ivt__input { flex: 1; }
.ivt__hint { margin: var(--drama-space-sm) 0 0; font-size: 12px; color: var(--drama-color-text-muted); }
.ivt__meta {
  display: flex; flex-wrap: wrap; gap: var(--drama-space-md);
  font-size: 12px; color: var(--drama-color-text-muted);
}
.ivt__meta code { font-family: var(--drama-font-family-mono, monospace); font-size: 11px; }
.ivt__meta strong { color: var(--drama-color-text); }
.ivt__sim { font-weight: 600; font-variant-numeric: tabular-nums; }
.ivt__ellipsis { display: block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
